import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import MarkdownArticle from "./MarkdownArticle";
import WaybackArchiveButton from "./WaybackArchiveButton";
import {
  BookDraftInput,
  useCreateBookMutation,
  useDeleteBookMutation,
  useGetBookForEditQuery,
  usePublishBookMutation,
  useReorderChaptersMutation,
  useUnpublishBookMutation,
  useUpdateBookMutation,
} from "../../services/rtkApi/blogApi";

/**
 * Create / edit a book at `/blog/books/new` and `/blog/manage/books/:id`.
 *
 * A book holds no prose of its own beyond the description — chapters are
 * articles, written in the post editor and attached there. What this page owns
 * is the framing (title, description, cover) and the **running order**, which
 * is the thing that actually changes as chapters accumulate.
 */
/** how long to sit on "loading" before offering a retry instead */
const LOAD_TIMEOUT_MS = 8_000;

const emptyBook: BookDraftInput = {
  title: "",
  subtitle: "",
  description: "",
  coverImageUrl: "",
  tags: [],
  complete: false,
};

const BookEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;

  const {
    data: book,
    isLoading,
    error: loadError,
    refetch: refetchBook,
  } = useGetBookForEditQuery(id || "", { skip: isNew });
  const [createBook, { isLoading: isCreating }] = useCreateBookMutation();
  const [updateBook, { isLoading: isUpdating }] = useUpdateBookMutation();
  const [publishBook, { isLoading: isPublishing }] = usePublishBookMutation();
  const [unpublishBook, { isLoading: isUnpublishing }] = useUnpublishBookMutation();
  const [reorderChapters, { isLoading: isReordering }] = useReorderChaptersMutation();
  const [deleteBook, { isLoading: isDeleting }] = useDeleteBookMutation();

  const [draft, setDraft] = useState<BookDraftInput>(emptyBook);
  const [tagInput, setTagInput] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loadTimedOut, setLoadTimedOut] = useState(false);

  /** Same bounded-loading guard as the post editor — see BlogEditorPage. */
  useEffect(() => {
    if (isNew || book || loadError) {
      setLoadTimedOut(false);
      return;
    }
    const timer = setTimeout(() => setLoadTimedOut(true), LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isNew, book, loadError]);

  useEffect(() => {
    if (!book) return;
    setDraft({
      title: book.title,
      subtitle: book.subtitle || "",
      description: book.description || "",
      coverImageUrl: book.coverImageUrl || "",
      tags: book.tags || [],
      complete: book.complete,
    });
  }, [book]);

  const busy =
    isCreating || isUpdating || isPublishing || isUnpublishing || isReordering || isDeleting;
  const isPublished = book?.status === "published";
  const chapters = book?.chapters ?? [];

  const set = <K extends keyof BookDraftInput>(key: K, value: BookDraftInput[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setStatus(null);
  };

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (!tag) return;
    if (!(draft.tags || []).includes(tag)) {
      set("tags", [...(draft.tags || []), tag].slice(0, 12));
    }
    setTagInput("");
  };

  const save = async (publish: boolean) => {
    if (!draft.title.trim()) {
      setError("give the book a title first");
      return;
    }
    setError(null);

    const payload: BookDraftInput = {
      title: draft.title.trim(),
      subtitle: draft.subtitle?.trim() || undefined,
      description: draft.description,
      coverImageUrl: draft.coverImageUrl?.trim() || undefined,
      tags: draft.tags,
      complete: draft.complete,
    };

    try {
      if (isNew) {
        const created = await createBook({
          ...payload,
          status: publish ? "published" : "draft",
        }).unwrap();
        navigate(`/blog/manage/books/${created._id}`, { replace: true });
        setStatus(publish ? "published" : "draft saved");
        return;
      }

      await updateBook({ id: id!, patch: payload }).unwrap();
      if (publish && !isPublished) {
        await publishBook(id!).unwrap();
        setStatus("published");
      } else {
        setStatus("saved");
      }
    } catch (err: any) {
      setError(err?.data?.error || "could not save the book");
    }
  };

  /** Moves a chapter one slot and persists the whole order, so numbers stay 1..n. */
  const moveChapter = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (!id || target < 0 || target >= chapters.length) return;

    const order = chapters.map((c) => c._id);
    [order[index], order[target]] = [order[target], order[index]];

    setError(null);
    try {
      await reorderChapters({ id, order }).unwrap();
      setStatus("order updated");
    } catch (err: any) {
      setError(err?.data?.error || "could not reorder the chapters");
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      const result = await deleteBook(id).unwrap();
      navigate("/blog/manage/books", { replace: true });
      if (result.detachedChapters) {
        // chapters survive as standalone posts — say so rather than implying loss
        console.info(`${result.detachedChapters} chapter(s) kept as standalone posts`);
      }
    } catch (err: any) {
      setError(err?.data?.error || "could not delete the book");
    }
  };

  // opening someone else's book by URL gets a 403 — say so rather than spinning
  if (!isNew && (loadError || loadTimedOut)) {
    const httpStatus = (loadError as { status?: number } | undefined)?.status;
    return (
      <PageContainer>
        <Inner>
          <BackLink to="/blog/manage/books">&larr; all books</BackLink>
          <Muted>
            {httpStatus === 403
              ? "this book belongs to someone else."
              : httpStatus === 404
                ? "that book does not exist."
                : "could not load that book."}
          </Muted>
          {httpStatus !== 403 && httpStatus !== 404 && (
            <Actions>
              <SecondaryButton
                type="button"
                onClick={() => {
                  setLoadTimedOut(false);
                  refetchBook();
                }}
              >
                try again
              </SecondaryButton>
            </Actions>
          )}
        </Inner>
      </PageContainer>
    );
  }

  if (!isNew && (isLoading || !book)) {
    return (
      <PageContainer>
        <Muted>loading book…</Muted>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Inner>
        <TopBar>
          <BackLink to="/blog/manage/books">&larr; all books</BackLink>
          <TopBarRight>
            {book && (
              <StatusPill $published={isPublished}>
                {isPublished ? "published" : "draft"}
              </StatusPill>
            )}
            {status && <SavedNote>{status}</SavedNote>}
          </TopBarRight>
        </TopBar>

        <Title>{isNew ? "start a book" : "edit book"}</Title>
        <Lede>
          A book is a topic you add chapters to over time. Each chapter is a normal
          post — write it from the post editor and pick this book there.
        </Lede>

        {error && <ErrorBanner role="alert">{error}</ErrorBanner>}

        <Field>
          <Label htmlFor="book-title">title</Label>
          <TextInput
            id="book-title"
            value={draft.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="what is the book about?"
            maxLength={200}
          />
        </Field>

        <Field>
          <Label htmlFor="book-subtitle">subtitle</Label>
          <TextInput
            id="book-subtitle"
            value={draft.subtitle || ""}
            onChange={(e) => set("subtitle", e.target.value)}
            placeholder="one line under the title"
            maxLength={300}
          />
        </Field>

        <Field>
          <Label htmlFor="book-cover">cover image url</Label>
          <TextInput
            id="book-cover"
            value={draft.coverImageUrl || ""}
            onChange={(e) => set("coverImageUrl", e.target.value)}
            placeholder="https://… (optional)"
          />
        </Field>

        <Field>
          <Label htmlFor="book-tags">tags</Label>
          <TagRow>
            {(draft.tags || []).map((tag) => (
              <TagChip key={tag}>
                {tag}
                <RemoveTag
                  type="button"
                  onClick={() => set("tags", (draft.tags || []).filter((t) => t !== tag))}
                  aria-label={`remove tag ${tag}`}
                >
                  ×
                </RemoveTag>
              </TagChip>
            ))}
            <TagInput
              id="book-tags"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  addTag();
                }
              }}
              onBlur={addTag}
              placeholder="add a tag…"
            />
          </TagRow>
        </Field>

        <Field>
          <Label htmlFor="book-description">description — markdown</Label>
          <DescriptionArea
            id="book-description"
            value={draft.description || ""}
            onChange={(e) => set("description", e.target.value)}
            placeholder="what this book is, who it is for, how it is being written…"
          />
        </Field>

        {draft.description?.trim() && (
          <PreviewWrap>
            <PreviewLabel>description preview</PreviewLabel>
            <MarkdownArticle content={draft.description} />
          </PreviewWrap>
        )}

        <CheckboxRow>
          <input
            id="book-complete"
            type="checkbox"
            checked={!!draft.complete}
            onChange={(e) => set("complete", e.target.checked)}
          />
          <CheckboxLabel htmlFor="book-complete">
            mark this book complete — no more chapters coming
          </CheckboxLabel>
        </CheckboxRow>

        <Actions>
          <PrimaryButton type="button" onClick={() => save(true)} disabled={busy}>
            {isPublished ? "save changes" : "publish book"}
          </PrimaryButton>
          {!isPublished && (
            <SecondaryButton type="button" onClick={() => save(false)} disabled={busy}>
              save draft
            </SecondaryButton>
          )}
          {isPublished && (
            <>
              <SecondaryButton
                type="button"
                onClick={() => unpublishBook(id!)}
                disabled={busy}
              >
                unpublish
              </SecondaryButton>
              <ViewLink to={`/blog/books/${book?.slug}`}>view book ↗</ViewLink>
            </>
          )}
        </Actions>

        {book && (
          <>
            <SectionTitle>
              chapters <Count>({chapters.length})</Count>
            </SectionTitle>
            <SectionNote>
              Order here is the reading order. Drafts are listed too but stay hidden on
              the public book page until published.
            </SectionNote>

            {chapters.length === 0 ? (
              <EmptyState>
                no chapters yet — <Link to="/blog/new">write the first one</Link> and pick
                this book in the editor.
              </EmptyState>
            ) : (
              <ChapterList>
                {chapters.map((chapter, i) => (
                  <ChapterRow key={chapter._id}>
                    <ChapterNumber>{chapter.chapterNumber ?? i + 1}</ChapterNumber>
                    <ChapterBody>
                      <ChapterTitle>{chapter.title}</ChapterTitle>
                      <ChapterMeta>
                        <MiniPill $published={chapter.status === "published"}>
                          {chapter.status}
                        </MiniPill>
                        <ChapterLink to={`/blog/manage/${chapter._id}`}>edit</ChapterLink>
                        {chapter.status === "published" && (
                          <ChapterLink to={`/blog/${chapter.slug}`}>view</ChapterLink>
                        )}
                      </ChapterMeta>
                    </ChapterBody>
                    <MoveButtons>
                      <MoveButton
                        type="button"
                        disabled={busy || i === 0}
                        onClick={() => moveChapter(i, -1)}
                        aria-label="move chapter up"
                      >
                        ↑
                      </MoveButton>
                      <MoveButton
                        type="button"
                        disabled={busy || i === chapters.length - 1}
                        onClick={() => moveChapter(i, 1)}
                        aria-label="move chapter down"
                      >
                        ↓
                      </MoveButton>
                    </MoveButtons>
                  </ChapterRow>
                ))}
              </ChapterList>
            )}

            <SectionTitle>archive</SectionTitle>
            <SectionNote>
              Archives the table of contents — which chapters existed, in what order, on
              this date. Each chapter archives separately from its own page.
            </SectionNote>
            <WaybackArchiveButton
              articleId={book._id}
              kind="book"
              initialWayback={book.wayback}
              disabled={!isPublished}
              disabledReason={!isPublished ? "publish the book before archiving it" : undefined}
            />

            <DangerZone>
              {confirmDelete ? (
                <>
                  <DangerButton type="button" onClick={handleDelete} disabled={busy}>
                    delete the book — keep the {chapters.length} chapter
                    {chapters.length === 1 ? "" : "s"}
                  </DangerButton>
                  <SecondaryButton type="button" onClick={() => setConfirmDelete(false)}>
                    cancel
                  </SecondaryButton>
                </>
              ) : (
                <SecondaryButton type="button" onClick={() => setConfirmDelete(true)}>
                  delete book
                </SecondaryButton>
              )}
            </DangerZone>
          </>
        )}
      </Inner>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f0f0f0;
  padding: 20px;

  @media (max-width: 700px) {
    padding: 12px;
  }
`;

const Inner = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding-bottom: 50px;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BackLink = styled(Link)`
  color: #466a5d;
  text-decoration: none;
  font-size: 0.9rem;

  &:hover {
    text-decoration: underline;
  }
`;

const ViewLink = styled(Link)`
  align-self: center;
  color: #466a5d;
  font-size: 0.88rem;
  text-decoration: underline;
`;

const StatusPill = styled.span<{ $published: boolean }>`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 3px 9px;
  border: 1px solid ${(p) => (p.$published ? "#466a5d" : "#b58b2a")};
  background: ${(p) => (p.$published ? "#d4f9eb" : "#fdf1d3")};
  color: #333;
`;

const SavedNote = styled.span`
  font-size: 0.82rem;
  color: #466a5d;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #222;
  text-transform: uppercase;
  margin: 10px 0 6px;
  text-shadow: 2px 2px 5px #3c3c3cae;
`;

const Lede = styled.p`
  font-size: 0.92rem;
  color: #666;
  margin: 0 0 22px;
  line-height: 1.5;
`;

const ErrorBanner = styled.div`
  background: #fde3e0;
  border: 1px solid #a3453b;
  color: #7a2e26;
  padding: 10px 14px;
  margin-bottom: 16px;
  font-size: 0.9rem;
`;

const Field = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #555;
  margin-bottom: 5px;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #ccc;
  background: white;
  font-size: 1rem;
  font-family: inherit;
  color: #222;

  &:focus {
    outline: none;
    border-color: #466a5d;
  }
`;

const DescriptionArea = styled.textarea`
  width: 100%;
  min-height: 180px;
  padding: 14px;
  border: 1px solid #ccc;
  background: white;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.92rem;
  line-height: 1.6;
  color: #222;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #466a5d;
  }
`;

const PreviewWrap = styled.div`
  margin-bottom: 18px;
`;

const PreviewLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #888;
  margin-bottom: 6px;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 6px;
  border: 1px solid #ccc;
  background: white;
  min-height: 42px;
`;

const TagChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  color: #444;
  background: #f5f5f5;
  border: 1px solid #ddd;
  padding: 3px 6px 3px 10px;
`;

const RemoveTag = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  color: #888;
  padding: 0 2px;

  &:hover {
    color: #a3453b;
  }
`;

const TagInput = styled.input`
  flex: 1;
  min-width: 140px;
  border: none;
  outline: none;
  font-size: 0.9rem;
  font-family: inherit;
  padding: 4px;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0 20px;
`;

const CheckboxLabel = styled.label`
  font-size: 0.9rem;
  color: #555;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const PrimaryButton = styled.button`
  padding: 10px 22px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid #466a5d;
  background: #9beece;
  color: #222;
  box-shadow: 2px 3px rgba(0, 0, 0, 0.5);

  &:hover:not(:disabled) {
    box-shadow: 3px 4px rgba(0, 0, 0, 0.5);
  }

  &:active:not(:disabled) {
    transform: translate(1px, 1px);
    box-shadow: 1px 1px rgba(0, 0, 0, 0.5);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(PrimaryButton)`
  background: white;
  border-color: #555;
  box-shadow: 2px 3px rgba(0, 0, 0, 0.25);
`;

const DangerButton = styled(PrimaryButton)`
  background: #fde3e0;
  border-color: #a3453b;
  color: #7a2e26;
  box-shadow: 2px 3px rgba(0, 0, 0, 0.25);
`;

const SectionTitle = styled.h2`
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #555;
  margin: 32px 0 6px;
  padding-top: 18px;
  border-top: 1px solid #ddd;
`;

const Count = styled.span`
  color: #999;
  font-weight: 400;
`;

const SectionNote = styled.p`
  font-size: 0.84rem;
  color: #777;
  margin: 0 0 12px;
  line-height: 1.5;
`;

const ChapterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChapterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  background: white;
  border: 1px solid #ccc;
  padding: 12px 16px;
  box-shadow: 1px 2px rgba(0, 0, 0, 0.1);
`;

const ChapterNumber = styled.span`
  font-size: 1.3rem;
  font-weight: 700;
  color: #cfe6dc;
  min-width: 28px;
  font-variant-numeric: tabular-nums;
`;

const ChapterBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChapterTitle = styled.h3`
  font-size: 1rem;
  color: #222;
  margin: 0 0 4px;
  line-height: 1.3;
`;

const ChapterMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const MiniPill = styled.span<{ $published: boolean }>`
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 1px 7px;
  border: 1px solid ${(p) => (p.$published ? "#466a5d" : "#b58b2a")};
  background: ${(p) => (p.$published ? "#d4f9eb" : "#fdf1d3")};
  color: #444;
`;

const ChapterLink = styled(Link)`
  font-size: 0.78rem;
  color: #466a5d;
  text-decoration: underline;
`;

const MoveButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const MoveButton = styled.button`
  width: 26px;
  height: 22px;
  font-size: 0.8rem;
  cursor: pointer;
  border: 1px solid #ccc;
  background: white;
  color: #555;

  &:hover:not(:disabled) {
    background: #d4f9eb;
    border-color: #466a5d;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.p`
  color: #888;
  font-style: italic;
  padding: 14px 0;

  a {
    color: #466a5d;
  }
`;

const DangerZone = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 34px;
  padding-top: 18px;
  border-top: 1px solid #ddd;
`;

const Muted = styled.p`
  text-align: center;
  color: #888;
  padding: 60px 0;
`;

export default BookEditorPage;
